import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { showToast } from '../config_and_helpers/helpers';
import { logError, logInfo } from '../config_and_helpers/logger';
import { normalizeCatalogEmailResponse } from '../utils/catalogQuoteEmailResponse';
import { formatOrderRequestErrors } from './order_request_client';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT = 'project_client';

/**
 * Flatten DRF validation errors for project save/status endpoints.
 *
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export const formatProjectSaveErrors = errPayload =>
  formatOrderRequestErrors(errPayload);

/**
 * Summarize `project_products` for logs (counts only, no line names or PII).
 *
 * @param {Array<Object>|undefined} lines
 * @returns {{ totalLines: number, inventoryLines: number, adHocLines: number, adHocLinesWithId: number }}
 */
export const summarizeProjectProductsForLog = lines => {
  const arr = Array.isArray(lines) ? lines : [];
  let inventoryLines = 0;
  let adHocLines = 0;
  let adHocLinesWithId = 0;
  arr.forEach(line => {
    if (line?.product_id != null && line.product_id !== '') {
      inventoryLines += 1;
    } else {
      adHocLines += 1;
      if (line?.id != null) adHocLinesWithId += 1;
    }
  });
  return {
    totalLines: arr.length,
    inventoryLines,
    adHocLines,
    adHocLinesWithId,
  };
};

/**
 * @param {Object|Array|string|null} errPayload
 * @param {*} error
 * @returns {{ status?: number, errorMessage: string, fieldCount: number }}
 */
const summarizeProjectClientError = (errPayload, error) => ({
  status: error?.response?.status,
  errorMessage:
    typeof errPayload === 'string'
      ? errPayload
      : error?.message || 'Request failed',
  fieldCount:
    errPayload && typeof errPayload === 'object' && !Array.isArray(errPayload)
      ? Object.keys(errPayload).length
      : 0,
});

/**
 * Retrieve projects with pagination and search.
 *
 * Sends a GET request to the backend API to retrieve a list of projects.
 * Supports optional pagination and search parameters.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Function} setProjects - React state's setter function being used to update the Projects state in the component.
 * @param {Object} options - An optional object that can contain nextPage and/or searchInput properties.
 * @returns {Promise<Object>} - An object with a boolean 'success', if the projects are successfully retrieved with either a 'reachedEnd' boolean or 'nextPage' string for next set of results; if not, it returns an array of error messages.
 * @throws Will throw an error if the request fails.
 */
export const getProjects = async (token, setProjects, options = {}) => {
  // Defaults
  const {
    searchInput = '',
    nextPage = null,
    ownerFilter = '',
    statusFilter = '',
    sortBy = '',
    sortDirection = '',
    signal = undefined,
  } = options;

  // Construct the request URL (check if next page URL exists, otherwise request the first page)
  let url = nextPage ? nextPage : `${BACKEND_URL}projects/?page_num=1`;

  // If a search input is provided add it to request URL as a query parameter
  if (searchInput) {
    url += `&search=${searchInput}`;
  }

  // If an owner filter is provided add it to request URL as a query parameter
  if (ownerFilter) {
    url += `&owner=${ownerFilter}`;
  }

  // If a status filter is provided add it to request URL as a query parameter
  if (statusFilter) {
    url += `&status=${statusFilter}`;
  }
  if (sortBy) {
    url += `&sort_by=${sortBy}`;
  }
  if (sortDirection) {
    url += `&sort_direction=${sortDirection}`;
  }

  try {
    // Get the projects from the server
    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      signal,
    });

    const nextCursor = response.data.next;

    // If there's no next cursor/page, it means we reached the end of the data
    if (!nextCursor) {
      // Check if it's a request for the next page or the first one
      if (nextPage) {
        // If it's a next page request, append new projects to current project list
        setProjects(prevProjects => {
          const updated = [...prevProjects, ...response.data.results];
          return updated;
        });
      } else {
        // If it's a request for the first page, reset the project list
        setProjects(response.data.results);
      }
      // Notify that we reached the end of the data
      return { success: true, reachedEnd: true };
    }

    // If it's not the end...
    // Check if it's a next page request or not
    if (!nextPage) {
      // If it's a request for the first page, reset the project list
      setProjects(response.data.results);
    } else {
      // If it's a next page request, append new projects to current project list
      setProjects(prevProjects => {
        const updated = [...prevProjects, ...response.data.results];
        return updated;
      });
    }

    // Return success state and next page's URL
    return { success: true, nextPage: response.data.next };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    // If the request fails, log the error messages and return them
    logError('project_client', 'getProjects failed', error.response?.data ?? error);
    if (error.response?.data) {
      return { success: false, error: error.response.data };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Retrieve project details.
 *
 * Sends a GET request to the backend API to retrieve the details of a specific project denoted by projectId.
 * If the request is successful, the project details are set in the state.
 *
 * The response includes a `quotes` array (QuoteListSerializer rows, each with optional `project: { id, name } | null`)
 * when the backend project-detail serializer embeds linked quotes. Prefer this over `getProjectQuotes` when present.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} projectId - The ID of the project whose details need to be fetched.
 * @param {Function} setProjectDetails - React state's setter function being used to update the ProjectDetails state in the component.
 * @returns {Promise<Object>} An object with a boolean 'success' if the project details are successfully retrieved; if not, it returns an array of error messages.
 * @throws Will throw an error if the request fails.
 */
export const getProjectDetails = async (token, projectId, setProjectDetails) => {
  try {
    // Get the project details from the server
    const response = await axios.get(`${BACKEND_URL}projects/${projectId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // Set the retrieved details in state
    setProjectDetails(response.data);

    logInfo('project_client', 'getProjectDetails success', {
      projectId,
      embeddedQuoteCount: Array.isArray(response.data?.quotes)
        ? response.data.quotes.length
        : null,
    });

    // Return a success state and data
    return { success: true, projectData: response.data };
  } catch (error) {
    // If the request fails, log the error messages and return them
    logError('project_client', 'getProjectDetails failed', error.response?.data ?? error);
    if (error.response?.data) {
      return { success: false, error: error.response.data };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Retrieve project products.
 *
 * Convenience endpoint that returns only the project's products.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} projectId - The ID of the project.
 * @returns {Promise<Object>} An object with a boolean 'success' and 'products' array if successful; if not, it returns an error.
 * @throws Will throw an error if the request fails.
 */
export const getProjectProducts = async (token, projectId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}projects/${projectId}/get_products/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // The API returns the products array directly, not wrapped in project_products
    const products = Array.isArray(response.data) ? response.data : (response.data.project_products || []);

    return { success: true, products };
  } catch (error) {
    logError('project_client', 'getProjectProducts failed', error.response?.data ?? error);
    if (error.response?.data) {
      return { success: false, error: error.response.data };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Retrieve project quotes.
 *
 * Returns quotes tied to the project.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} projectId - The ID of the project.
 * @returns {Promise<Object>} An object with a boolean 'success' and 'quotes' array if successful; if not, it returns an error.
 * @throws Will throw an error if the request fails.
 */
export const getProjectQuotes = async (token, projectId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}projects/${projectId}/get_quotes/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return { success: true, quotes: response.data.results || response.data };
  } catch (error) {
    logError('project_client', 'getProjectQuotes failed', error.response?.data ?? error);
    if (error.response?.data) {
      return { success: false, error: error.response.data };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Retrieve project orders.
 *
 * Returns orders tied to the project.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} projectId - The ID of the project.
 * @returns {Promise<Object>} An object with a boolean 'success' and 'orders' array if successful; if not, it returns an error.
 * @throws Will throw an error if the request fails.
 */
export const getProjectOrders = async (token, projectId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}projects/${projectId}/get_orders/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return { success: true, orders: response.data.results || response.data };
  } catch (error) {
    logError('project_client', 'getProjectOrders failed', error.response?.data ?? error);
    if (error.response?.data) {
      return { success: false, error: error.response.data };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Create a new project.
 *
 * Sends a POST request to the backend API to create a new project with optional product requirements.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} projectData - The data for the new project.
 * @returns {Promise<Object|Array>} An object with a key "success" indicating whether project creation was successful and "toast" with a function to display a toast message. If the request fails, returns an array with error messages.
 * @throws Will throw an error if the request fails.
 */
export const createProject = async (token, projectData) => {
  const lineSummary = summarizeProjectProductsForLog(projectData?.project_products);
  try {
    logInfo(LOG_CONTEXT, 'createProject start', lineSummary);
    const response = await axios.post(`${BACKEND_URL}projects/`, projectData, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // Function to be called to display a toast notification
    const result = {
      success: true,
      projectData: response.data,
      toast: () =>
        showToast('Project created successfully!', 'success', 'top-right', 3000),
    };

    logInfo(LOG_CONTEXT, 'createProject success', {
      projectId: response.data?.id,
      ...lineSummary,
    });
    return result;
  } catch (error) {
    const errPayload = error.response?.data ?? error.message;
    logError(LOG_CONTEXT, 'createProject failed', {
      ...summarizeProjectClientError(errPayload, error),
      ...lineSummary,
    });
    return formatProjectSaveErrors(errPayload);
  }
};

/**
 * Update an existing project.
 *
 * Sends a PATCH request to the backend API to update a particular project denoted by projectId with the provided updatedProjectData.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} projectId - The ID of the project to be updated.
 * @param {Object} updatedProjectData - The updated data of the project.
 * @returns {Promise<Object|Array>} An object with a key "success" indicating whether project update was successful and "toast" with a function to display a toast message. If the request fails, returns an array with error messages.
 * @throws Will throw an error if the request fails.
 */
export const updateProject = async (
  token,
  projectId,
  updatedProjectData
) => {
  const lineSummary = summarizeProjectProductsForLog(
    updatedProjectData?.project_products
  );
  try {
    logInfo(LOG_CONTEXT, 'updateProject start', {
      projectId,
      ...lineSummary,
    });
    const response = await axios.patch(
      `${BACKEND_URL}projects/${projectId}/`,
      updatedProjectData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    // Function to be called to display a toast notification
    const result = {
      success: true,
      updatedProject: response.data,
      toast: () =>
        showToast('Project updated successfully!', 'success', 'top-right', 3000),
    };

    logInfo(LOG_CONTEXT, 'updateProject success', {
      projectId,
      ...lineSummary,
    });
    return result;
  } catch (error) {
    const errPayload = error.response?.data ?? error.message;
    logError(LOG_CONTEXT, 'updateProject failed', {
      projectId,
      ...summarizeProjectClientError(errPayload, error),
      ...lineSummary,
    });
    return formatProjectSaveErrors(errPayload);
  }
};

/**
 * Delete an existing project.
 *
 * Sends a DELETE request to the backend API to remove a particular project denoted by projectId.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} projectId - The ID of the project to be deleted.
 * @returns {Promise<Object|Array>} An object with a key "success" and a function "toast" to display a toast message if the project deletion is successful. If the request fails, returns an array with error messages.
 * @throws Will throw an error if the request fails.
 */
/**
 * Fetches a list of projects for selection purposes (e.g., quote/order project association).
 *
 * This function sends a GET request to the backend API to retrieve a list of projects.
 * The response is formatted for use in dropdown/select components.
 *
 * @async
 * @param {string} token - The authentication token of the user.
 * @param {function} setProjectSelectList - Callback function to set the project select list.
 * @returns {Promise<Object>} - An object with success status, or error messages if the request fails.
 * @throws Will throw an error if the request fails.
 */
export const getProjectSelectList = async (token, setProjectSelectList) => {
  try {
    const response = await axios.get(`${BACKEND_URL}projects/?page_num=1&page_size=1000`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // Format projects for dropdown: [{ value: id, label: "Project Name" }]
    // Tolerate both paginated (response.data.results) and direct-array response shapes
    const raw = response.data?.results ?? response.data;
    const arr = Array.isArray(raw) ? raw : [];
    const formattedProjects = arr.map(project => ({
      value: project.id,
      label: project.name || `Project ${project.id}`,
    }));

    if (setProjectSelectList) {
      setProjectSelectList(formattedProjects);
    }

    return { success: true, projects: formattedProjects };
  } catch (error) {
    logError('project_client', 'getProjectSelectList failed', error.response?.data ?? error);
    if (setProjectSelectList) {
      setProjectSelectList([]);
    }
    return { success: false, error: error.response?.data || error.message };
  }
};

export const deleteProject = async (token, projectId) => {
  try {
    // Send a DELETE request to backend API to delete a particular project
    await axios.delete(`${BACKEND_URL}projects/${projectId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // If successful, return a success response
    return {
      success: true,
      toast: () =>
        showToast('Project deleted successfully!', 'success', 'top-right', 3000),
    };
  } catch (error) {
    // If the request fails, log the error messages and return them
    logError('project_client', 'deleteProject failed', error.response?.data ?? error);
    if (error.response?.data) {
      return Object.values(error.response.data);
    }
    return [error.message];
  }
};

/**
 * Request supplier quote emails for ad-hoc project lines.
 *
 * Proposed backend path: POST `projects/{id}/request_quote_emails/`
 * with lines keyed by `project_product_id`.
 *
 * @async
 * @param {string} token
 * @param {string|number} projectId
 * @param {{ lines: Array<{project_product_id:number, quantity:number}>, update_status?: boolean }} payload
 * @returns {Promise<{success:boolean,data?:Object,error?:*,messages?:string[]}>}
 */
export const requestQuoteEmailsFromProject = async (token, projectId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'requestQuoteEmailsFromProject start', {
      projectId,
      lineCount: Array.isArray(payload?.lines) ? payload.lines.length : 0,
      updateStatus: Boolean(payload?.update_status),
    });
    const response = await axios.post(
      `${BACKEND_URL}projects/${projectId}/request_quote_emails/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    logInfo(LOG_CONTEXT, 'requestQuoteEmailsFromProject success', {
      projectId,
      supplierCount: response.data?.suppliers?.length ?? 0,
      corporateDemandRef: response.data?.corporate_demand_ref ?? '',
    });
    const data = normalizeCatalogEmailResponse(response.data);
    return { success: true, data };
  } catch (error) {
    const errPayload = error.response?.data ?? error.message;
    const messages = formatProjectSaveErrors(errPayload);
    logError(LOG_CONTEXT, 'requestQuoteEmailsFromProject failed', {
      projectId,
      ...summarizeProjectClientError(errPayload, error),
      lineCount: Array.isArray(payload?.lines) ? payload.lines.length : 0,
    });
    return {
      success: false,
      status: error.response?.status,
      error: errPayload,
      messages,
    };
  }
};

/**
 * Update a project line status via POST `projects/{id}/update_product_status/`.
 *
 * Prefer `projectProductId` for all lines (required when `product_id` is null on ad-hoc rows).
 * Legacy inventory-only path: pass `productId` and omit `projectProductId`.
 *
 * @async
 * @param {string} token - Lab user auth token
 * @param {string|number} projectId - Project pk
 * @param {number|null} productId - Inventory `Product` pk (legacy); omit when using `projectProductId`
 * @param {string} status - One of `PROJECT_PRODUCT_STATUS_OPTIONS` values
 * @param {number|null} [projectProductId] - `ProjectProduct` pk; use for ad-hoc lines and preferred for inventory
 * @returns {Promise<{success:boolean,data?:Object,toast?:Function,error?:*}>}
 */
export const updateProductStatus = async (
  token,
  projectId,
  productId,
  status,
  projectProductId = null
) => {
  try {
    logInfo(LOG_CONTEXT, 'updateProductStatus start', {
      projectId,
      projectProductId: projectProductId ?? null,
      productId: productId ?? null,
      status,
    });
    const body = { status };
    if (projectProductId != null) {
      body.project_product_id = projectProductId;
    } else if (productId != null) {
      body.product_id = productId;
    }
    const response = await axios.post(
      `${BACKEND_URL}projects/${projectId}/update_product_status/`,
      body,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    logInfo(LOG_CONTEXT, 'updateProductStatus success', {
      projectId,
      projectProductId: projectProductId ?? null,
      status,
    });
    return {
      success: true,
      data: response.data,
      toast: () =>
        showToast(
          'Product status updated successfully!',
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    const errPayload = error.response?.data ?? error.message;
    logError(LOG_CONTEXT, 'updateProductStatus failed', {
      projectId,
      projectProductId: projectProductId ?? null,
      ...summarizeProjectClientError(errPayload, error),
    });
    return {
      success: false,
      status: error.response?.status,
      error: errPayload,
      messages: formatProjectSaveErrors(errPayload),
    };
  }
};

